import React from 'react';
import { type Call_V2, type Contact, type User } from 'src/api/generated';
import {
  Table,
  type TableColumnProps,
} from 'src/modules/shared/components/molecules/table/table';
import { Text } from 'src/modules/shared/components/atoms/text';
import {
  formatDate,
  formatTime,
} from 'src/modules/shared/utils/formatting/date.util';
import CallDirectionDisplay from 'src/components/molecules/calls/call-direction-display/call-direction-display';
import {
  getDurationText,
  getStatusClass,
  getCategoryText,
  getCategoryColor,
  isCallCompleted,
} from 'src/utils/calls';
import CallInfoModals from 'src/components/molecules/calls/call-info-modals/call-info-modals';
import { Chip } from '@material-tailwind/react';
import { PiMagnifyingGlass, PiUser } from 'react-icons/pi';
import { type color } from '@material-tailwind/react/types/components/chip';
import { formatFullName } from 'src/utils/formatting/name';
import ContactFullNameInput from 'src/modules/shared/components/molecules/contacts/contact-full-name-input';

interface CallsTableProps {
  calls: Call_V2[];
  contactsMap: Map<number, Contact>;
  users: User[];
  loading: boolean;
  isLoadingMore?: boolean;
}

export const CallsTable: React.FC<CallsTableProps> = ({
  calls,
  contactsMap,
  users,
  loading,
  isLoadingMore = false,
}) => {
  const columns: TableColumnProps<Call_V2>[] = [
    {
      // TODO: check when implementing i18n
      header: 'Fecha',
      accessor: 'startTime',
      render: (_value, call) => (
        <div className="flex flex-col">
          <Text size="small">{formatDate(call.startTime, 'DD-MM-YYYY')}</Text>
          <Text size="small" className="text-gray-500">
            {formatTime(call.startTime)}
          </Text>
        </div>
      ),
    },
    {
      // TODO: check when implementing i18n
      header: 'Dirección',
      accessor: 'direction',
      render: (_value, call) => {
        const direction =
          call.direction === 'outgoing' ? 'outgoing' : 'incoming';
        return (
          <CallDirectionDisplay
            direction={direction}
            redirectedTo={call.redirectedTo}
          />
        );
      },
    },
    {
      // TODO: check when implementing i18n
      header: 'Número',
      accessor: 'to',
      render: (_value, call) => (
        <Text size="small" className="whitespace-nowrap">
          {call.direction === 'outgoing' ? call.to : call.from}
        </Text>
      ),
    },
    {
      // TODO: check when implementing i18n
      header: 'Contacto',
      accessor: 'contactId',
      render: (_value, call) => {
        const contact = contactsMap.get(call.contactId);
        if (!contact) {
          return (
            <div className="flex items-center gap-2 text-gray-500">
              <PiUser className="h-4 w-4" />
              <Text size="small">Sin contacto</Text>
            </div>
          );
        }
        return (
          <ContactFullNameInput
            contactId={contact.id}
            firstName={contact.firstName ?? null}
            lastName={contact.lastName ?? null}
          />
        );
      },
    },
    {
      // TODO: check when implementing i18n
      header: 'Duración',
      accessor: 'duration',
      render: (_value, call) => {
        const duration = getDurationText(String(call.status), call.duration);
        const statusClass = getStatusClass(String(call.status));
        return (
          <Text size="small" className={statusClass}>
            {duration}
          </Text>
        );
      },
    },
    {
      // TODO: check when implementing i18n
      header: 'Categoría',
      accessor: 'category',
      render: (_value, call) => {
        if (!call.category) return <Text size="small">-</Text>;
        const categoryText = getCategoryText(call.category);
        if (!categoryText) return <Text size="small">-</Text>;
        const categoryColor: color = getCategoryColor(call.category);
        return (
          <Chip
            variant="ghost"
            color={categoryColor}
            size="sm"
            value={categoryText}
            className="w-fit"
          />
        );
      },
    },
    {
      // TODO: check when implementing i18n
      header: 'Agente',
      accessor: 'userId',
      render: (_value, call) => {
        if (!call.userId) return <Text size="small">-</Text>;
        const user = users.find((candidate) => candidate.id === call.userId);
        if (!user) return <Text size="small">-</Text>;
        return (
          <Text size="small" className="whitespace-nowrap">
            {formatFullName(user.firstName, user.lastName)}
          </Text>
        );
      },
    },
    {
      header: '',
      accessor: 'id',
      render: (_value, call) => (
        <CallInfoModals
          callId={call.id}
          callAgentScore={0}
          visible={isCallCompleted(call)}
          buttonClassName="!p-0"
          iconClassName="h-5 w-5"
          playerHeight={32}
          playerWidth={200}
        />
      ),
    },
  ];

  return (
    <Table<Call_V2>
      variant="neutral"
      columns={columns}
      data={calls}
      keyExtractor={(call) => call.id.toString()}
      isLoading={loading && calls.length === 0}
      isLoadingMore={isLoadingMore}
      // TODO: check when implementing i18n
      emptyStateMessage="No se encontraron llamadas"
      emptyStateDescription="Ajusta los filtros o intenta de nuevo más tarde"
      emptyStateIcon={<PiMagnifyingGlass className="w-full h-full" />}
    />
  );
};
